import type React from "react";
import { useCallback, useMemo, useState } from "react";

import * as _ from "lodash";

import { CountryDropdownButton, CountryDropdownItem, Dropdown } from "@similarweb/ui-components";

import type { IUnlockModalConfigTypes } from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import UnlockModal from "app/components/React/UnlockModalProvider/UnlockModalProvider";
import { ConversionSegmentsUtils } from "app/pages/conversion/ConversionSegmentsUtils";
import type { IWizardCountries } from "app/pages/conversion/wizard/CustomGroupWizard";

import { CountryItemWrapper, DropdownContainer } from "./StyledComponents";

interface IWizardCountriesDropdownProps {
    items: {
        [id: string]: { id: number; text: string };
    };
    onSelectedItemsChange: (items: object[]) => void;
    selectedCountry: string;
    availableCountries: IWizardCountries;
}

export const WizardCountriesDropdown: React.FunctionComponent<IWizardCountriesDropdownProps> = ({
    items,
    onSelectedItemsChange,
    selectedCountry,
    availableCountries,
}) => {
    const sortedCountriesList = useMemo(
        () =>
            _.sortBy(
                Object.keys(items).map((id) => items[id]),
                "text",
            ),
        [items],
    );
    const availableCountriesCodes = useMemo(
        () => Object.keys(availableCountries).map((id) => availableCountries[id].id),
        [availableCountries],
    );
    const userCountries = useMemo(() => ConversionSegmentsUtils.getUserCountries(), []);
    const [isCountryHookOpen, setIsCountryHookOpen] = useState(false);
    const openCountryHook = useCallback(
        (evt: FixmeType) => {
            evt.stopPropagation();
            setIsCountryHookOpen(true);
        },
        [setIsCountryHookOpen],
    );
    const closeCountryHook = useCallback(() => setIsCountryHookOpen(false), [setIsCountryHookOpen]);
    const onItemClick = (item: FixmeType) => {
        onSelectedItemsChange({
            ...item,
            ...items[item.id],
        });
    };
    const selectedCountryKey = Object.keys(items).find((id) => {
        return id === selectedCountry;
    });
    // @ts-ignore FIXME: strict migration - Type 'undefined' cannot be used as an index type — selectedCountryKey comes from Array.find and selectedCountry is expected to match an existing item
    const selectedId = items[selectedCountryKey];

    return (
        <DropdownContainer>
            <Dropdown
                // @ts-expect-error Wrong type
                selectedIds={{ [selectedCountryKey]: selectedId }}
                shouldScrollToSelected={true}
                onClick={onItemClick}
                itemsComponent={CountryDropdownItem}
            >
                {[
                    <CountryDropdownButton
                        key={selectedId.text}
                        countryId={selectedId.id}
                    >
                        {selectedId.text}
                    </CountryDropdownButton>,
                    ...sortedCountriesList.map(({ id, text }) => {
                        // @ts-expect-error Type string is not assignable to type number
                        const isAvailable = availableCountriesCodes.includes(id);
                        const isPermitted = userCountries.includes(+id);
                        const itemParams = {
                            id: +id,
                            code: `${id}`,
                            permitted: isPermitted,
                            text,
                        };
                        return (
                            <div
                                key={`${id}_${text}`}
                                id={`${id}`}
                            >
                                <CountryItemWrapper
                                    isAvailable={isAvailable}
                                    {...(!isPermitted && { onClick: openCountryHook })}
                                >
                                    <CountryDropdownItem {...itemParams}>{text}</CountryDropdownItem>
                                </CountryItemWrapper>
                            </div>
                        );
                    }),
                ]}
            </Dropdown>
            <UnlockModal
                isOpen={isCountryHookOpen}
                onCloseClick={closeCountryHook}
                activeSlide={"Countries" as IUnlockModalConfigTypes["CountryFilters"]}
                location="Hook PRO/Funnel Analysis/Country Filter"
                {...unlockModalConfig().CountryFilters}
            />
        </DropdownContainer>
    );
};
